// Changing a vendor without code (plan v2 §3.3): an API's settings, the vendor's base address, its key. Every change
// is tested first with one safe call ("Test"); on a live vendor a second person approves it; the audit log keeps
// old → new. A key is typed once: only its first 6 characters are ever kept or shown.
import { z } from 'zod'
import { Id, IsoDateTime } from './common'
import { ApiSettings } from './vendor-apis'

export const BaseUrl = z
  .string()
  .trim()
  .regex(/^https?:\/\/[^\s/]+(\/\S*)?$/, 'An address like https://api.exotel.com')

export const VendorChangeKind = z.enum(['api_settings', 'base_url', 'key'])
export type VendorChangeKind = z.infer<typeof VendorChangeKind>

export const ApiSettingsChange = z.object({
  kind: z.literal(VendorChangeKind.enum.api_settings),
  api_id: Id,
  settings: ApiSettings,
})
export const BaseUrlChange = z.object({ kind: z.literal(VendorChangeKind.enum.base_url), base_url: BaseUrl })
export const KeyChange = z.object({
  kind: z.literal(VendorChangeKind.enum.key),
  api_key: z.string().trim().min(8, 'The key has at least 8 characters'),
})
export type ApiSettingsChange = z.infer<typeof ApiSettingsChange>
export type BaseUrlChange = z.infer<typeof BaseUrlChange>
export type KeyChange = z.infer<typeof KeyChange>

export const ChangeInput = z.discriminatedUnion('kind', [ApiSettingsChange, BaseUrlChange, KeyChange])
export type ChangeInput = z.infer<typeof ChangeInput>

/** The answer to "Test": one safe call with the new values (no person, no message). */
export const ChangeTest = z.object({
  test_id: Id,
  ok: z.boolean(),
  ms: z.number().int().min(0),
  message: z.string(),
})
export type ChangeTest = z.infer<typeof ChangeTest>

/** Save: the change and the test that passed with exactly these values. */
export const RequestChangeInput = z.object({ change: ChangeInput, test_id: Id })
export type RequestChangeInput = z.infer<typeof RequestChangeInput>

export const ChangeStatus = z.enum(['waiting', 'applied', 'rejected', 'cancelled'])
export type ChangeStatus = z.infer<typeof ChangeStatus>

/** One value that changes; a key shows its first 6 characters only. */
export const ChangeLine = z.object({ field: z.string(), before: z.string(), after: z.string() })
export type ChangeLine = z.infer<typeof ChangeLine>

export const VendorChange = z.object({
  id: Id,
  vendor_id: Id,
  vendor_name: z.string(),
  api_id: Id.nullable(),
  api_name: z.string().nullable(),
  kind: VendorChangeKind,
  lines: z.array(ChangeLine).min(1),
  test: ChangeTest.omit({ test_id: true }),
  status: ChangeStatus,
  made_by: Id,
  made_by_name: z.string(),
  made_at: IsoDateTime,
  decided_by_name: z.string().nullable(),
  decided_at: IsoDateTime.nullable(),
})
export type VendorChange = z.infer<typeof VendorChange>

/** approve / reject: a second person; cancel: the person who asked. */
export const ChangeDecision = z.enum(['approve', 'reject', 'cancel'])
export type ChangeDecision = z.infer<typeof ChangeDecision>

export const ChangeFilter = z.object({
  vendor_id: Id.optional(),
  api_id: Id.optional(),
  status: ChangeStatus.optional(),
})
export type ChangeFilter = z.infer<typeof ChangeFilter>

export interface VendorChangesApi {
  testVendorChange(vendorId: string, change: ChangeInput): Promise<ChangeTest>
  /** applied at once on a test vendor; waits for a second person on a live one */
  requestVendorChange(vendorId: string, input: RequestChangeInput): Promise<VendorChange>
  decideVendorChange(changeId: string, decision: ChangeDecision): Promise<VendorChange>
  /** newest first */
  listVendorChanges(filter?: ChangeFilter): Promise<VendorChange[]>
}
